import { useState, useEffect } from 'react';
import { DocumentIcon, ArrowPathIcon, TrashIcon } from '@heroicons/react/24/outline';
import { ingestionService, JobStatus } from '../../services/ingestionService';
import { ConfirmDialog } from '../ui/ConfirmDialog';

interface JobsListProps {
  refreshTrigger?: number;
}

export function JobsList({ refreshTrigger }: JobsListProps) {
  const [jobs, setJobs] = useState<JobStatus[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selected, setSelected] = useState<Set<string>>(new Set());

  // ConfirmDialog state
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [confirmTarget, setConfirmTarget] = useState<string | null>(null); // null = batch

  const loadJobs = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await ingestionService.listJobs(1, 50);
      setJobs(response.jobs);
    } catch (err: any) {
      const isTimeout =
        err.code === 'ECONNABORTED' ||
        err.code === 'NETWORK_ERROR' ||
        err.message?.includes('Network Error') ||
        err.message?.includes('timeout');
      setError(
        isTimeout
          ? 'Tempo limite ao carregar documentos. Verifique sua conexão e tente novamente.'
          : 'Erro ao carregar documentos.'
      );
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadJobs();
  }, [refreshTrigger]);

  // ── Selection helpers ──────────────────────────────────────────────────────

  const toggleSelect = (filename: string) => {
    setSelected(prev => {
      const next = new Set(prev);
      next.has(filename) ? next.delete(filename) : next.add(filename);
      return next;
    });
  };

  const toggleAll = () => {
    if (selected.size === jobs.length) {
      setSelected(new Set());
    } else {
      setSelected(new Set(jobs.map(j => j.filename)));
    }
  };

  // ── Delete handlers ────────────────────────────────────────────────────────

  const askDeleteOne = (filename: string) => {
    setConfirmTarget(filename);
    setConfirmOpen(true);
  };

  const askDeleteSelected = () => {
    setConfirmTarget(null);
    setConfirmOpen(true);
  };

  const handleConfirm = async () => {
    setConfirmOpen(false);
    try {
      if (confirmTarget) {
        await ingestionService.deleteDocument(confirmTarget);
      } else {
        await ingestionService.deleteDocuments(Array.from(selected));
        setSelected(new Set());
      }
      await loadJobs();
    } catch (err) {
      console.error('Failed to delete document(s):', err);
    }
  };

  // ── Status helpers ─────────────────────────────────────────────────────────

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'indexed':
      case 'completed':
        return 'bg-green-100 text-green-800';
      case 'failed':
        return 'bg-red-100 text-red-800';
      case 'processing':
        return 'bg-blue-100 text-blue-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const getStatusLabel = (status: string) => {
    switch (status) {
      case 'indexed': return 'Indexado';
      case 'uploaded': return 'Aguardando';
      case 'processing': return 'Processando';
      case 'completed': return 'Concluído';
      case 'failed': return 'Falhou';
      default: return status;
    }
  };

  const formatDate = (dateString: string) =>
    new Date(dateString).toLocaleString('pt-BR');

  // ── Confirm dialog message ─────────────────────────────────────────────────

  const confirmMessage = confirmTarget
    ? `Excluir permanentemente "${confirmTarget}"? O documento será removido do pipeline RAG.`
    : `Excluir permanentemente ${selected.size} documento(s) selecionado(s)? Esta ação não pode ser desfeita.`;

  return (
    <>
      <div className="bg-white shadow rounded-lg">
        <div className="px-6 py-4 border-b border-gray-200">
          <div className="flex items-center justify-between">
            <h3 className="text-lg font-medium text-gray-900">
              Documentos ({jobs.length})
            </h3>
            <div className="flex items-center gap-2">
              {selected.size > 0 && (
                <button
                  onClick={askDeleteSelected}
                  className="flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium text-white bg-red-600 hover:bg-red-700 rounded-md transition-colors"
                >
                  <TrashIcon className="h-4 w-4" />
                  Excluir {selected.size} selecionado{selected.size > 1 ? 's' : ''}
                </button>
              )}
              <button
                onClick={loadJobs}
                className="text-emerald-600 hover:text-emerald-700 transition-colors"
                title="Atualizar lista"
              >
                <ArrowPathIcon className="h-5 w-5" />
              </button>
            </div>
          </div>
        </div>

        <div className="divide-y divide-gray-200">
          {loading && jobs.length === 0 ? (
            <div className="p-6 text-center text-gray-500">Carregando...</div>
          ) : error ? (
            <div className="p-6 text-center text-gray-500">
              <p>{error}</p>
              <button
                onClick={loadJobs}
                className="mt-2 text-sm text-emerald-600 hover:text-emerald-500"
              >
                Tentar novamente
              </button>
            </div>
          ) : jobs.length === 0 ? (
            <div className="p-6 text-center text-gray-500">
              Nenhum documento encontrado. Faça upload de arquivos PDF, DOCX ou TXT.
            </div>
          ) : (
            <>
              {/* Select all row */}
              <div className="px-4 py-2 bg-gray-50 flex items-center gap-3">
                <input
                  type="checkbox"
                  checked={selected.size === jobs.length && jobs.length > 0}
                  onChange={toggleAll}
                  className="h-4 w-4 rounded border-gray-300 text-emerald-600 focus:ring-emerald-500"
                />
                <span className="text-xs text-gray-500">
                  {selected.size > 0 ? `${selected.size} selecionado(s)` : 'Selecionar todos'}
                </span>
              </div>

              {jobs.map((job) => (
                <div key={job.job_id} className="p-4 hover:bg-gray-50">
                  <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                    <div className="flex items-start gap-3 flex-1 min-w-0">
                      <input
                        type="checkbox"
                        checked={selected.has(job.filename)}
                        onChange={() => toggleSelect(job.filename)}
                        className="mt-1 h-4 w-4 rounded border-gray-300 text-emerald-600 focus:ring-emerald-500 flex-shrink-0"
                      />
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <DocumentIcon className="h-5 w-5 text-gray-400 flex-shrink-0" />
                          <span className="font-medium text-gray-900 truncate">{job.filename}</span>
                          <span className={`px-2 py-1 text-xs font-medium rounded-full flex-shrink-0 ${getStatusColor(job.status)}`}>
                            {getStatusLabel(job.status)}
                          </span>
                        </div>
                        <p className="mt-1 text-sm text-gray-500 ml-7">
                          Enviado em {formatDate(job.created_at)}
                        </p>

                        {job.status === 'processing' && job.progress > 0 && (
                          <div className="mt-2 ml-7 space-y-1">
                            <div className="flex items-center justify-between">
                              <span className="text-xs text-gray-500">Processando</span>
                              <span className="text-xs text-gray-500">{job.progress}%</span>
                            </div>
                            <div className="w-full bg-gray-200 rounded-full h-1.5">
                              <div
                                className="bg-emerald-500 h-1.5 rounded-full transition-all duration-300"
                                style={{ width: `${job.progress}%` }}
                              />
                            </div>
                          </div>
                        )}

                        {job.status === 'failed' && job.error && (
                          <div className="mt-2 ml-7 p-2 bg-red-50 rounded-md">
                            <p className="text-xs text-red-700">{job.error}</p>
                          </div>
                        )}
                      </div>
                    </div>

                    <div className="flex items-center gap-2 ml-4 flex-shrink-0">
                      <button
                        onClick={() => askDeleteOne(job.filename)}
                        className="inline-flex items-center gap-1 px-3 py-1 text-sm text-red-400 hover:text-red-700"
                        title="Excluir documento"
                      >
                        <TrashIcon className="h-4 w-4" />
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </>
          )}
        </div>
      </div>

      <ConfirmDialog
        open={confirmOpen}
        title="Excluir documento(s)"
        message={confirmMessage}
        confirmLabel="Excluir"
        variant="danger"
        onConfirm={handleConfirm}
        onCancel={() => setConfirmOpen(false)}
      />
    </>
  );
}
